/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { View, StyleSheet } from 'react-native';

import ClickAway from './ClickAway';
import { themeColors } from './Constants';
import { XButton } from './Button';
import Text from './Text';

import { KEY_ESCAPE } from '../../support/KeyCodes';
import { disablePageScroll, enablePageScroll } from '../../support/Utils';

type Props = {
  title?: string;
  isVisible?: boolean;
  nonCancellable?: boolean;
  useNonDefaultComponent?: boolean;
  onClose?: (() => void) | null | undefined;
  closeButtonStyle?: StyleSet;
  style?: StyleSet;
  children: React.ReactNode | null | undefined;
};

type ModalComponentProps = Pick<Props, 'children' | 'style'>;

let modalCount = 0;
function ModalFull(props: Props) {
  const {
    title,
    isVisible,
    nonCancellable = false,
    useNonDefaultComponent = false,
    onClose,
    closeButtonStyle,
    style,
    children,
  } = props;

  const onClickAway = () => {
    let { nonCancellable, onClose } = props;
    if (!nonCancellable && onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!nonCancellable && isVisible && onClose) {
        e.keyCode === KEY_ESCAPE && onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    modalCount += 1;
    if (modalCount === 1) {
      // First instance mounted
      disablePageScroll();
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      modalCount -= 1;
      if (modalCount === 0) {
        // Last instance unmounted
        enablePageScroll();
      }
    };
  }, [isVisible, nonCancellable, onClose]);

  let body = document.body;
  if (!body || !isVisible) {
    return null;
  }

  let header = !useNonDefaultComponent ? (
    <View style={styles.headerContainer}>
      <Text type="heading" style={styles.headerModalText}>
        {title}
      </Text>
    </View>
  ) : undefined;

  let content = !useNonDefaultComponent ? (
    <View style={styles.body}>{children}</View>
  ) : (
    children
  );

  return createPortal(
    <View style={styles.backdrop}>
      <ClickAway onClickAway={onClickAway}>
        <View style={[styles.root, style]} accessibilityLabel="Modal">
          {header}
          {content}
          {!nonCancellable && (
            <XButton
              onPress={onClose ?? undefined}
              color={themeColors.white}
              style={[styles.closeWrapper, closeButtonStyle]}
              tabIndex={-1}
            />
          )}
        </View>
      </ClickAway>
    </View>,
    body,
  );
}

const Header = ({ children, style }: ModalComponentProps) => (
  <View style={[styles.headerContainer, style]}>{children}</View>
);

const Body = ({ children, style }: ModalComponentProps) => (
  <View style={[styles.body, style]}>{children}</View>
);

let styles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: themeColors.transparent,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    position: 'fixed' as any,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: themeColors.withOpacity.black40,
    zIndex: 10,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 15,
    paddingHorizontal: 30,
    backgroundColor: themeColors.black,
    borderBottomWidth: StyleSheet.hairlineWidth * 2,
    borderColor: themeColors.lightGrey,
  },
  headerModalText: {
    fontSize: 16,
    color: themeColors.white,
  },
  body: {
    flex: 1,
  },
  closeWrapper: {
    position: 'absolute',
    top: 2,
    right: 30,
  },
});

ModalFull.Header = Header;
ModalFull.Body = Body;

export default ModalFull;
